import React, { useState } from 'react';
import { Edit, PlusCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { adminUsersApi, type StandortOption, type Verantwortungsbereich } from '../../../lib/rbac/userAdmin';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bereiche: Verantwortungsbereich[];
  standorte: StandortOption[];
  onChanged: (next: Verantwortungsbereich[]) => void;
}

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/** Verantwortungsbereiche: Standortmengen als Träger des Unternehmens-Scopes (G.7). */
const AreasDialog: React.FC<Props> = ({ open, onOpenChange, bereiche, standorte, onChanged }) => {
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [ids, setIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const standortName = new Map(standorte.map((s) => [s.id, s.name]));

  const reset = () => {
    setEditId(null);
    setName('');
    setIds([]);
  };

  const save = async () => {
    if (!name.trim()) return toast.error('Bitte einen Namen angeben');
    setSaving(true);
    try {
      const payload = { name: name.trim(), standort_ids: ids };
      const next = editId
        ? await adminUsersApi.updateBereich(editId, payload)
        : await adminUsersApi.createBereich(payload);
      onChanged(next);
      reset();
      toast.success('Verantwortungsbereich gespeichert');
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (bereich: Verantwortungsbereich) => {
    if (!confirm(`Verantwortungsbereich „${bereich.name}" löschen? ${bereich.mitglieder} Benutzer verlieren ihn.`)) return;
    try {
      onChanged(await adminUsersApi.deleteBereich(bereich.id));
      if (editId === bereich.id) reset();
      toast.success('Verantwortungsbereich gelöscht');
    } catch (e) {
      toast.error(errorText(e, 'Löschen fehlgeschlagen'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Verantwortungsbereiche</DialogTitle>
          <DialogDescription>
            Ein Bereich fasst Standorte zusammen. Er bestimmt, worauf sich der Unternehmens-Scope
            einer Rolle wie CEO bezieht.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {bereiche.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Bereiche.</p>}
          {bereiche.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 rounded-md border p-2">
              <div>
                <div className="font-medium">{b.name}</div>
                <div className="text-xs text-muted-foreground">
                  {b.standort_ids.map((id) => standortName.get(id) ?? id).join(', ') || 'keine Standorte'}
                  {' · '}{b.mitglieder} Benutzer
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setEditId(b.id); setName(b.name); setIds(b.standort_ids); }}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(b)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3 rounded-md border p-3">
          <div className="text-sm font-medium">{editId ? 'Bereich bearbeiten' : 'Neuer Bereich'}</div>
          <div>
            <Label>Name</Label>
            <Input uiSize="sm" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {standorte.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={ids.includes(s.id)}
                  onCheckedChange={(checked) => setIds(checked ? [...ids, s.id] : ids.filter((id) => id !== s.id))}
                />
                {s.name}
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          {editId && <Button variant="outline" onClick={reset}>Neu statt bearbeiten</Button>}
          <Button onClick={save} disabled={saving}>
            <PlusCircle className="mr-2 h-4 w-4" /> {editId ? 'Speichern' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AreasDialog;
